// Formas de pago del POS. POS → Configuración → Formas de pago.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BuscadorCuenta } from '@/components/ui/BuscadorCuenta';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { CODIGOS_SRI_PAGO, Campo, CuentaContableOpt, FormaPago, FormaPagoLocal, LocationOpt, Modal, Switch, TIPO_FORMA_PAGO, codigoSriSugerido, cuentasContables, fieldCls, locationsList, pos } from './shared';

const vacio = { codigo: '', nombre: '', tipo: 'efectivo', requiere_referencia: false, orden: 0, es_activo: true, cuenta_contable_id: '', codigo_sri: '01', comision_pct: 0 };

export function FormasPago() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [tab, setTab] = useState<'catalogo' | 'locales'>('catalogo');
  const { data: rows = [], isFetching } = useQuery<FormaPago[]>({ queryKey: ['pos-formas-pago'], queryFn: () => pos.list<FormaPago>('formas_pago', '?select=*&order=orden,nombre') });
  const { data: cuentas = [] } = useQuery<CuentaContableOpt[]>({ queryKey: ['pos-cuentas-contables'], queryFn: cuentasContables });
  const cuentaLabel = (id: string | null) => { const c = cuentas.find((x) => x.id === id); return c ? `${c.codigo} · ${c.nombre}` : '—'; };

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (c?: FormaPago) => {
    if (c) { setEditId(c.id); setForm({ codigo: c.codigo ?? '', nombre: c.nombre, tipo: c.tipo, requiere_referencia: c.requiere_referencia, orden: c.orden, es_activo: c.es_activo, cuenta_contable_id: c.cuenta_contable_id ?? '', codigo_sri: c.codigo_sri ?? '', comision_pct: c.comision_pct ?? 0 }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      if (!form.codigo_sri) throw new Error('Selecciona el código SRI (Tabla 24) de la forma de pago');
      const body = { ...form, codigo: form.codigo || null, orden: Number(form.orden) || 0, cuenta_contable_id: form.cuenta_contable_id || null, comision_pct: Number(form.comision_pct) || 0 };
      return editId ? pos.update('formas_pago', editId, body) : pos.create('formas_pago', body);
    },
    onSuccess: () => { toast.success('Forma de pago guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['pos-formas-pago'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => pos.remove('formas_pago', id),
    onSuccess: () => { toast.success('Forma de pago eliminada'); void qc.invalidateQueries({ queryKey: ['pos-formas-pago'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CreditCard className="w-5 h-5" /> Formas de pago</h1>
        {tab === 'catalogo' && <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>}
      </div>

      <div className="flex items-center gap-2 border-b">
        {([['catalogo', 'Catálogo'], ['locales', 'Por local']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'locales' && <MatrizPorLocal formas={rows.filter((r) => r.es_activo)} />}

      {tab === 'catalogo' && <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Formas de pago ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Orden</th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Código SRI</th><th className="py-2 pr-3 text-right">Comisión %</th><th className="py-2 pr-3">Cuenta contable</th><th className="py-2 pr-3">Ref.</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin formas de pago</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{c.orden}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{c.codigo ?? '—'}</td>
                  <td className="py-2 pr-3">{c.nombre}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize">{c.tipo}</span></td>
                  <td className="py-2 pr-3 font-mono text-xs">{c.codigo_sri ?? '—'}</td>
                  <td className="py-2 pr-3 text-right">{c.comision_pct != null ? `${Number(c.comision_pct)}%` : '—'}</td>
                  <td className="py-2 pr-3 text-xs">{cuentaLabel(c.cuenta_contable_id)}</td>
                  <td className="py-2 pr-3">{c.requiere_referencia ? 'Sí' : 'No'}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>}

      {abierto && (
        <Modal title={editId ? 'Editar forma de pago' : 'Nueva forma de pago'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} /></Campo>
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => set({ orden: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            <Campo label="Tipo">
              <select className={fieldCls} value={form.tipo} onChange={(e) => { const tipo = e.target.value; set({ tipo, ...(editId ? {} : { codigo_sri: codigoSriSugerido(tipo) }) }); }}>
                {TIPO_FORMA_PAGO.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código SRI (Tabla 24) *">
                <select className={fieldCls} value={form.codigo_sri} onChange={(e) => set({ codigo_sri: e.target.value })}>
                  <option value="">— Selecciona —</option>
                  {CODIGOS_SRI_PAGO.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  Es el código que va en la factura electrónica (SRI). Débito=16, crédito=19, transferencias/DeUna=20, efectivo=01.
                </span>
              </Campo>
              <Campo label="Comisión % (recargo tarjeta)">
                <input type="number" step="0.01" min="0" max="100" className={fieldCls} value={form.comision_pct} onChange={(e) => set({ comision_pct: Number(e.target.value) })} />
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  Porcentaje que se recarga al cobrar con esta forma (0 = sin recargo).
                </span>
              </Campo>
            </div>
            <Campo label="Cuenta contable (débito al cerrar caja)">
              <BuscadorCuenta value={form.cuenta_contable_id} onChange={(id) => set({ cuenta_contable_id: id })}
                allowClear placeholder="— Según tipo (Caja / Descuentos) —" />
            </Campo>
            <div className="flex items-center justify-between border-t pt-3">
              <div className="flex flex-col gap-2">
                <Switch checked={form.requiere_referencia} onChange={(v) => set({ requiere_referencia: v })} label="Requiere referencia (nº aprobación)" />
                <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activa" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || !form.codigo_sri} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Matriz formas de pago × locales. Regla: forma SIN marcas = disponible en
// TODOS los locales; con marcas = solo en los marcados. Clic en encabezado de
// local marca/desmarca su columna completa; clic en el nombre de la forma, su
// fila; y se puede copiar la configuración de un local a otro.
// ---------------------------------------------------------------------------
function MatrizPorLocal({ formas }: { formas: FormaPago[] }) {
  const qc = useQueryClient();
  const { data: locales = [] } = useQuery<LocationOpt[]>({ queryKey: ['pos-locales'], queryFn: locationsList });
  const { data: asigs = [], isFetching } = useQuery<FormaPagoLocal[]>({
    queryKey: ['pos-fp-locales'],
    queryFn: () => pos.list<FormaPagoLocal>('forma_pago_locales', '?select=id,forma_pago_id,location_id'),
    staleTime: 0,
  });
  const [copiaDe, setCopiaDe] = useState('');
  const [copiaA, setCopiaA] = useState('');

  const marcada = (f: string, l: string) => asigs.find((a) => a.forma_pago_id === f && a.location_id === l);
  const invalidar = () => void qc.invalidateQueries({ queryKey: ['pos-fp-locales'] });

  const mutar = useMutation({
    mutationFn: async (ops: { crear: { forma_pago_id: string; location_id: string }[]; borrar: string[] }) => {
      for (const id of ops.borrar) await pos.remove('forma_pago_locales', id);
      for (const c of ops.crear) await pos.create('forma_pago_locales', c);
    },
    onSuccess: invalidar,
    onError: (e) => { toast.error(getErrorMessage(e)); invalidar(); },
  });

  const toggle = (f: string, l: string) => {
    const a = marcada(f, l);
    mutar.mutate(a ? { crear: [], borrar: [a.id] } : { crear: [{ forma_pago_id: f, location_id: l }], borrar: [] });
  };
  // Columna (local): si todas las formas están marcadas → limpiar; si no → completar
  const toggleLocal = (l: string) => {
    const faltan = formas.filter((f) => !marcada(f.id, l));
    if (faltan.length > 0) mutar.mutate({ crear: faltan.map((f) => ({ forma_pago_id: f.id, location_id: l })), borrar: [] });
    else mutar.mutate({ crear: [], borrar: asigs.filter((a) => a.location_id === l).map((a) => a.id) });
  };
  // Fila (forma): completar todos los locales o limpiar (= vuelve a "todos por defecto")
  const toggleForma = (f: string) => {
    const faltan = locales.filter((l) => !marcada(f, l.id));
    if (faltan.length > 0) mutar.mutate({ crear: faltan.map((l) => ({ forma_pago_id: f, location_id: l.id })), borrar: [] });
    else mutar.mutate({ crear: [], borrar: asigs.filter((a) => a.forma_pago_id === f).map((a) => a.id) });
  };
  const copiar = () => {
    if (!copiaDe || !copiaA || copiaDe === copiaA) { toast.error('Elige local origen y destino distintos'); return; }
    const destinoActual = asigs.filter((a) => a.location_id === copiaA);
    const origen = asigs.filter((a) => a.location_id === copiaDe);
    mutar.mutate({
      borrar: destinoActual.map((a) => a.id),
      crear: origen.map((a) => ({ forma_pago_id: a.forma_pago_id, location_id: copiaA })),
    });
    toast.success('Configuración copiada');
  };

  const sinMarcas = (f: string) => !asigs.some((a) => a.forma_pago_id === f);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Disponibilidad por local {isFetching && <Loader2 className="inline w-4 h-4 animate-spin ml-1" />}</CardTitle>
        <div className="flex items-center gap-2 text-sm">
          <select className={`${fieldCls} h-9 w-44`} value={copiaDe} onChange={(e) => setCopiaDe(e.target.value)}>
            <option value="">Copiar de…</option>
            {locales.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
          </select>
          <span className="text-muted-foreground">→</span>
          <select className={`${fieldCls} h-9 w-44`} value={copiaA} onChange={(e) => setCopiaA(e.target.value)}>
            <option value="">a…</option>
            {locales.filter((l) => l.id !== copiaDe).map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
          </select>
          <Button size="sm" variant="outline" disabled={!copiaDe || !copiaA || mutar.isPending} onClick={copiar}>Copiar</Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-left">Forma de pago</th>
              {locales.map((l) => (
                <th key={l.id} className="py-2 px-2 text-center">
                  <button className="hover:text-foreground hover:underline" title="Marcar/desmarcar todo el local" onClick={() => toggleLocal(l.id)}>
                    {l.nombre}
                  </button>
                </th>
              ))}
              <th className="py-2 pl-2 text-left">Efecto</th>
            </tr>
          </thead>
          <tbody>
            {formas.map((f) => (
              <tr key={f.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="py-2 pr-3">
                  <button className="font-medium hover:underline" title="Marcar/desmarcar todos los locales" onClick={() => toggleForma(f.id)}>
                    {f.nombre}
                  </button>
                </td>
                {locales.map((l) => (
                  <td key={l.id} className="py-2 px-2 text-center">
                    <input type="checkbox" className="h-4 w-4 cursor-pointer" checked={!!marcada(f.id, l.id)}
                      disabled={mutar.isPending} onChange={() => toggle(f.id, l.id)} />
                  </td>
                ))}
                <td className="py-2 pl-2 text-xs">
                  {sinMarcas(f.id)
                    ? <span className="text-emerald-700 dark:text-emerald-400">Todos los locales (sin restricción)</span>
                    : <span className="text-muted-foreground">Solo los locales marcados</span>}
                </td>
              </tr>
            ))}
            {formas.length === 0 && <tr><td colSpan={locales.length + 2} className="py-8 text-center text-muted-foreground">Sin formas de pago activas</td></tr>}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-muted-foreground">
          Una forma <span className="font-medium">sin ninguna marca</span> está disponible en todos los locales.
          Al marcar casillas, queda restringida solo a esos locales — tanto en la pantalla de cobro como en el servidor.
        </p>
      </CardContent>
    </Card>
  );
}
